/**
 * Create Liquidation Address Wizard
 * 
 * Multi-step wizard for creating liquidation addresses.
 * 
 * @module addresses/wizard
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';

import WizardStepIndicator from './WizardStepIndicator';
import DestinationTypeSelector from './DestinationTypeSelector';
import AssetSelector from './AssetSelector';
import BankAccountSelector from './BankAccountSelector';
import WalletAddressInput from './WalletAddressInput';
import ReviewStep from './ReviewStep';
import SuccessScreen from './SuccessScreen';

import { createOrGetAddress } from '@/lib/rpc/address';
import type { WizardFormData } from './types';
import { DEFAULT_FORM_DATA } from './types';

interface CreateAddressWizardProps {
  onClose?: () => void;
  onSuccess?: (address: string) => void;
}

const WIZARD_STEPS = [
  { id: 1, label: 'Destination' },
  { id: 2, label: 'Asset' },
  { id: 3, label: 'Account' },
  { id: 4, label: 'Review' },
];

export const CreateAddressWizard: React.FC<CreateAddressWizardProps> = ({
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<WizardFormData>(DEFAULT_FORM_DATA);
  const [label, setLabel] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdAddress, setCreatedAddress] = useState<string | null>(null);

  const updateFormData = useCallback(<K extends keyof WizardFormData>(
    key: K, 
    value: WizardFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const canGoNext = useCallback((): boolean => {
    switch (currentStep) {
      case 1:
        return !!formData.destinationType;
      case 2:
        return !!formData.asset && !!formData.network;
      case 3:
        if (formData.destinationType === 'fiat') {
          return !!formData.externalAccountId;
        }
        if (formData.destinationType === 'crypto') {
          return !!formData.destinationAddress && !!formData.destinationChain;
        }
        return false;
      case 4:
        return true;
      default:
        return false;
    }
  }, [currentStep, formData]);

  const goNext = useCallback(() => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const goBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 4) {
      setCurrentStep(step);
    }
  }, []);

  const handleCreate = useCallback(async () => {
    setIsCreating(true);

    try {
      const params = {
        asset: formData.asset,
        network: formData.network,
        label: label || undefined,
        ...(formData.destinationType === 'fiat' && {
          payoutRouteId: formData.externalAccountId,
          currency: formData.currency,
        }),
      };

      console.log('[CreateAddressWizard] Creating address with params:', params);
      console.log('[CreateAddressWizard] formData.externalAccountId (payoutRouteId):', formData.externalAccountId);

      const result = await createOrGetAddress(params);

      console.log('[CreateAddressWizard] createOrGetAddress result:', {
        ok: result.ok,
        hasData: !!result.data,
        address: result.data?.address,
        error: result.error?.message,
      });

      if (!result.ok) {
        console.error('[CreateAddressWizard] Creation failed:', result.error);
        throw result.error || new Error('Failed to create address');
      }

      const address = result.data?.address;
      if (!address) {
        console.error('[CreateAddressWizard] No address in response:', result.data);
        throw new Error('No address returned');
      }

      console.log('[CreateAddressWizard] SUCCESS! Setting createdAddress:', address);
      setCreatedAddress(address);
      toast.success('Address created successfully!');
      onSuccess?.(address);
    } catch (err: unknown) {
      console.error('[CreateAddressWizard] Error:', err);
      toast.error(err.message || 'Failed to create address');
    } finally {
      setIsCreating(false);
    }
  }, [formData, label, onSuccess]);

  const handleLinkNewAccount = useCallback(() => {
    // Pass returnPath so user returns to wizard after linking account
    navigate('/add-bank-account', { state: { returnPath: '/addresses/create' } });
  }, [navigate]);

  const handleViewAllAddresses = useCallback(() => {
    navigate('/dashboard/receive');
  }, [navigate]);

  const handleCreateAnother = useCallback(() => {
    setCurrentStep(1);
    setFormData(DEFAULT_FORM_DATA);
    setLabel('');
    setCreatedAddress(null);
  }, []);

  const renderStep = () => {
    if (createdAddress) {
      return (
        <SuccessScreen
          address={createdAddress}
          formData={formData}
          onViewAllAddresses={handleViewAllAddresses}
          onCreateAnother={handleCreateAnother}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <DestinationTypeSelector
            value={formData.destinationType}
            onChange={(type) => updateFormData('destinationType', type)}
          />
        );
      
      case 2:
        return (
          <AssetSelector
            selectedAsset={formData.asset}
            selectedNetwork={formData.network}
            onAssetChange={(asset) => updateFormData('asset', asset)}
            onNetworkChange={(network) => updateFormData('network', network)}
          />
        );
      
      case 3:
        if (formData.destinationType === 'fiat') {
          return (
            <BankAccountSelector
              selectedPayoutRouteId={formData.externalAccountId}
              selectedPaymentRail={formData.paymentRail}
              selectedCurrency={formData.currency}
              onPayoutRouteSelect={(id, rail, currency) => {
                updateFormData('externalAccountId', id);
                updateFormData('paymentRail', rail);
                updateFormData('currency', currency);
              }}
              onLinkNewAccount={handleLinkNewAccount}
            />
          );
        }
        
        if (formData.destinationType === 'crypto') {
          return (
            <WalletAddressInput
              destinationAddress={formData.destinationAddress || ''}
              destinationChain={formData.destinationChain || 'ethereum'}
              destinationAsset={formData.destinationAsset || 'USDC'}
              onAddressChange={(addr) => updateFormData('destinationAddress', addr)}
              onChainChange={(chain) => updateFormData('destinationChain', chain)}
              onAssetChange={(asset) => updateFormData('destinationAsset', asset)}
            />
          );
        }
        
        return null;
      
      case 4:
        return (
          <ReviewStep
            formData={formData}
            label={label}
            onLabelChange={setLabel}
            onEditStep={goToStep}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-full">
      {!createdAddress && (
        <div className={cn("shrink-0 p-4 sm:p-6 border-b", semanticTokens.border.default)}>
          <div className="flex items-center justify-between mb-4">
            <h1 className={cn("text-lg sm:text-xl font-bold", semanticTokens.fg.default)}>
              Create Address
            </h1>
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8" aria-label="Close">
                <SFIcon icon={sfIcons.close} size="sm" />
              </Button>
            )}
          </div>
          
          <WizardStepIndicator steps={WIZARD_STEPS} currentStep={currentStep} />
        </div>
      )}

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="max-w-xl mx-auto">
          {renderStep()}
        </div>
      </div>

      {!createdAddress && (
        <div className={cn(
          "shrink-0 p-4 sm:p-6 border-t",
          semanticTokens.border.default,
          semanticTokens.bg.surface
        )}>
          <div className="max-w-xl mx-auto flex items-center justify-between gap-4">
            <Button variant="ghost" onClick={goBack} disabled={currentStep === 1 || isCreating} className="gap-2">
              <SFIcon icon={sfIcons.back} size="sm" />
              <span className="hidden sm:inline">Back</span>
            </Button>

            <span className={cn("text-sm sm:hidden", semanticTokens.fg.muted)}>
              {currentStep} / 4
            </span>

            {currentStep < 4 ? (
              <Button onClick={goNext} disabled={!canGoNext()} className="gap-2">
                Continue
                <SFIcon icon={sfIcons.next} size="sm" />
              </Button>
            ) : (
              <Button onClick={handleCreate} disabled={isCreating} className="gap-2 min-w-[140px]">
                {isCreating ? (
                  <>
                    <SFIcon icon={sfIcons.loader} size="sm" className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Address
                    <SFIcon icon={sfIcons.next} size="sm" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateAddressWizard;

